import { t as tr } from './i18n';
import { effectivePrice, extraCost, lineCost } from './analyse';
import { shortfall } from './coverage';
import type { PurchaseOrder } from './po';
import type { Rfq, SupplierRef } from './types';

/**
 * Comanda împărțită (cerută de proprietar, 2026-10-16): fiecare poziție de la
 * furnizorul care o dă mai ieftin, câte o comandă pentru fiecare. Tabelul
 * comparativ arăta deja „dacă împarți comanda pe poziții”; de aici se și face.
 *
 * Alegerea rămâne una: `chosen.supplier` e partea cea mai mare (așa merg mai
 * departe listele, panoul, scorecard-ul), iar `chosen.split` spune ce poziții
 * ia fiecare. Fără împărțire, cererea are o singură parte: furnizorul ales,
 * cu toate pozițiile.
 */

/** Ce ia un furnizor, cum vine de la om. */
export interface SplitInput {
  supplierId: string;
  itemIds: string[];
}

/** Ce ia un furnizor, cum se arată. */
export interface SplitPart {
  supplier: SupplierRef;
  itemIds: string[];
}

export interface SplitPartCost {
  supplierId: string;
  /** pozițiile părții, în lei fără TVA */
  subtotal: number;
  /** transportul și costurile pe comandă ale furnizorului */
  extras: number;
  total: number;
  /** câte poziții din parte nu le-a cotat furnizorul ei */
  unpriced: number;
}

const offerOf = (rfq: Rfq, supplierId: string) =>
  rfq.sends.find((s) => s.supplier.id === supplierId)?.offer ?? null;

/** Costul fiecărei părți și totalul; `total` null dacă vreo poziție n-are preț. */
export function splitCost(rfq: Rfq, parts: SplitInput[]): { parts: SplitPartCost[]; total: number | null } {
  const out = parts.map((p) => {
    const offer = offerOf(rfq, p.supplierId);
    let subtotal = 0;
    let unpriced = 0;
    for (const id of p.itemIds) {
      const it = rfq.items.find((x) => x.id === id);
      const v = it && offer ? lineCost(offer, it) : null;
      if (v == null) unpriced += 1;
      else subtotal += v;
    }
    const extras = offer ? extraCost(offer).lei : 0;
    return { supplierId: p.supplierId, subtotal, extras, total: subtotal + extras, unpriced };
  });
  return {
    parts: out,
    total: out.some((p) => p.unpriced) ? null : out.reduce((n, p) => n + p.total, 0),
  };
}

/**
 * Propunerea: fiecare poziție la cel mai mic preț (după comanda minimă și
 * ambalaj), doar din ofertele pe toată cantitatea. Partea cea mai mare prima.
 * null dacă iese un singur furnizor sau o poziție n-are nicio ofertă.
 */
export function bestSplit(rfq: Rfq): SplitInput[] | null {
  const by = new Map<string, string[]>();
  for (const it of rfq.items) {
    let best: { supplierId: string; price: number } | null = null;
    for (const s of rfq.sends) {
      if (!s.offer || shortfall(s.offer, it)) continue;
      const p = effectivePrice(s.offer, it);
      if (p != null && (!best || p < best.price)) best = { supplierId: s.supplier.id, price: p };
    }
    if (!best) return null;
    by.set(best.supplierId, [...(by.get(best.supplierId) ?? []), it.id]);
  }
  if (by.size < 2) return null;
  const parts = [...by.entries()].map(([supplierId, itemIds]) => ({ supplierId, itemIds }));
  const cost = new Map(splitCost(rfq, parts).parts.map((c) => [c.supplierId, c.total]));
  return parts.sort((a, b) => cost.get(b.supplierId)! - cost.get(a.supplierId)!);
}

/** De ce nu se poate salva împărțirea; null dacă se poate. */
export function splitError(rfq: Rfq, parts: SplitInput[]): string | null {
  const used = parts.filter((p) => p.itemIds.length);
  if (used.some((p) => !rfq.sends.some((s) => s.supplier.id === p.supplierId))) {
    return tr('Cererea nu a plecat către unul dintre furnizorii aleși.');
  }
  for (const it of rfq.items) {
    const n = used.filter((p) => p.itemIds.includes(it.id)).length;
    if (n === 0) return tr('Poziția „{0}” nu are furnizor.', it.desc);
    if (n > 1) return tr('Poziția „{0}” e la doi furnizori.', it.desc);
  }
  if (new Set(used.map((p) => p.supplierId)).size < 2) {
    return tr('Împărțirea are nevoie de cel puțin doi furnizori.');
  }
  return null;
}

/**
 * Valoarea cererii împărțite, lei fără TVA: comanda emisă a fiecărei părți,
 * altfel estimarea ei de la alegere. null dacă o parte necomandată n-are estimare.
 */
export function splitValue(
  parts: { supplierId: string; total: number | null }[],
  issued: { supplierId: string; netLei: number }[],
): number | null {
  let sum = 0;
  for (const p of parts) {
    const v = issued.find((o) => o.supplierId === p.supplierId)?.netLei ?? p.total;
    if (v == null) return null;
    sum += v;
  }
  return Math.round(sum * 100) / 100;
}

/**
 * Comanda cererii, pentru etapa din listă: 'issued', 'pending' sau null (încă
 * de comandat). `orders` = comenzile din cerere (nu suplimentări) emise sau la
 * aprobare. La cea împărțită (`split` = furnizorii părților), o parte fără
 * comandă ține cererea „de comandat”; altfel una la aprobare o ține acolo.
 */
export function orderFact(
  orders: { status: string; supplierId: string }[],
  split: string[] | null,
): 'issued' | 'pending' | null {
  if (!split?.length) {
    return orders.some((o) => o.status === 'issued') ? 'issued' : orders.length ? 'pending' : null;
  }
  if (split.some((id) => !orders.some((o) => o.supplierId === id))) return null;
  return split.every((id) => orders.some((o) => o.supplierId === id && o.status === 'issued'))
    ? 'issued'
    : 'pending';
}

/** Părțile cererii: cea împărțită, sau furnizorul ales cu toate pozițiile; [] fără alegere. */
export function rfqParts(rfq: Pick<Rfq, 'chosen' | 'items'>): SplitPart[] {
  if (!rfq.chosen) return [];
  if (rfq.chosen.split?.length) return rfq.chosen.split;
  return [{ supplier: rfq.chosen.supplier, itemIds: rfq.items.map((it) => it.id) }];
}

/** Comanda din cerere (nu suplimentare) către furnizorul unei părți, într-o stare dată. */
export const partOrder = (
  rfq: { orders?: PurchaseOrder[] },
  supplierId: string,
  status: 'issued' | 'pending',
): PurchaseOrder | null =>
  rfq.orders?.find((o) => o.status === status && o.kind !== 'supplement' && o.supplier.id === supplierId) ??
  null;

/** Părțile încă fără comandă: nici emisă, nici la aprobare. */
export const partsToOrder = (rfq: Rfq): SplitPart[] =>
  rfqParts(rfq).filter(
    (p) => !partOrder(rfq, p.supplier.id, 'issued') && !partOrder(rfq, p.supplier.id, 'pending'),
  );
